import { assertExists } from '@sparelabs/core'
import { UnexpectedError } from '@sparelabs/error-types'
import { FlagDownConstants } from '../flagDown'
import { IWindow, OffsetDirection, TimeConstraintType } from '../scheduling'
import { ArriveByPickupConstraintType, RequestIntentType } from '../types'
import {
  IMatchedRequestForConstraintsData,
  IRequestConstraints,
  IRequestConstraintsLegacy,
  IUnmatchedRequestForConstraintsData,
} from './RequestConstraintsTypes'
import { RequestConstraintUtils } from './RequestConstraintUtils'
import { RequestSemantics } from './RequestSemantics'

export class RequestConstraintsTransformer {
  public static transformForUnmatched(
    request: IUnmatchedRequestForConstraintsData,
    intentType: RequestIntentType,
    estimateCreatedTs: number,
  ): IRequestConstraints {
    const arriveByPickupConstraintType = request.arriveByPickupConstraintType ?? ArriveByPickupConstraintType.Detour
    RequestConstraintUtils.validateWindowsForType(
      request.unmatchedPickupWindow,
      request.unmatchedDropoffWindow,
      intentType,
      arriveByPickupConstraintType,
      request.id,
    )

    const constraints = RequestConstraintUtils.toValidConstraints({
      pickupWindow: request.unmatchedPickupWindow,
      dropoffWindow: request.unmatchedDropoffWindow,
      maxTimeInVehicle: request.travelDuration + request.travelDurationFlexibility,
    })

    const isLeaveAt = constraints.pickupWindow !== null && constraints.dropoffWindow === null
    return isLeaveAt ? this.shiftForUnmatchedLeaveAt(constraints, request, estimateCreatedTs) : constraints
  }

  public static transformForMatched(
    request: IMatchedRequestForConstraintsData,
    intentType: RequestIntentType,
  ): IRequestConstraints {
    const arriveByPickupConstraintType = request.arriveByPickupConstraintType ?? ArriveByPickupConstraintType.Detour
    RequestConstraintUtils.validateWindowsForType(
      request.matchedPickupWindow,
      request.matchedDropoffWindow,
      intentType,
      arriveByPickupConstraintType,
      request.id,
    )

    return RequestConstraintUtils.toValidConstraints({
      pickupWindow: request.matchedPickupWindow,
      dropoffWindow: request.matchedDropoffWindow,
      maxTimeInVehicle: request.travelDuration + request.travelDurationFlexibility,
    })
  }

  private static shiftForUnmatchedLeaveAt(
    constraints: IRequestConstraints,
    request: IUnmatchedRequestForConstraintsData,
    estimateCreatedTs: number,
  ): IRequestConstraints {
    /**
     * If the desired pickup time is in the past, we need to bump it forward so that it aims no earlier
     * than the estimated created time:
     *
     * If it is in the past, assume that the rider wants a trip as soon as possible. Such a discrepancy
     * may arise due to rematching (e.g. on duty cancellation), or simply due to delay between the
     * time the estimate was created, and the first time the request gets matched.
     */
    const originalPickupWindow = assertExists(constraints.pickupWindow)
    const desiredTimeInPastAmount = Math.max(estimateCreatedTs - originalPickupWindow.desired, 0)
    const pickupWindow: IWindow = {
      min: originalPickupWindow.min + desiredTimeInPastAmount,
      desired: originalPickupWindow.desired + desiredTimeInPastAmount,
      max: originalPickupWindow.max + desiredTimeInPastAmount,
    }

    /**
     * Flagdown requests get an extra high detour flexibility when they are initially matched,
     * just to ensure a high match rate for flagdowns
     */
    const detourFlexibility = RequestSemantics.isFlagDownRequest(request)
      ? FlagDownConstants.FLAG_DOWN_PRE_MATCH_TRAVEL_DURATION_FLEXIBILITY
      : request.travelDurationFlexibility

    return {
      pickupWindow,
      dropoffWindow: null,
      maxTimeInVehicle: request.travelDuration + detourFlexibility,
    }
  }

  public static toRequestConstraintsLegacy(requestConstraints: IRequestConstraints): IRequestConstraintsLegacy {
    if (requestConstraints.pickupWindow && !requestConstraints.dropoffWindow) {
      return {
        type: RequestIntentType.LeaveAt,
        pickupConstraint: {
          type: TimeConstraintType.Absolute,
          min: requestConstraints.pickupWindow.min,
          desired: requestConstraints.pickupWindow.desired,
          max: requestConstraints.pickupWindow.max,
        },
        dropoffConstraint: {
          type: TimeConstraintType.Relative,
          direction: OffsetDirection.After,
          maxOffset: requestConstraints.maxTimeInVehicle,
        },
      }
    }

    if (!requestConstraints.pickupWindow && requestConstraints.dropoffWindow) {
      return {
        type: RequestIntentType.ArriveBy,
        pickupConstraint: {
          type: TimeConstraintType.Relative,
          direction: OffsetDirection.Before,
          maxOffset: requestConstraints.maxTimeInVehicle,
        },
        dropoffConstraint: {
          type: TimeConstraintType.Absolute,
          min: requestConstraints.dropoffWindow.min,
          desired: requestConstraints.dropoffWindow.desired,
          max: requestConstraints.dropoffWindow.max,
        },
      }
    }

    if (requestConstraints.pickupWindow && requestConstraints.dropoffWindow) {
      return {
        type: RequestIntentType.ArriveBy,
        pickupConstraint: {
          type: TimeConstraintType.Absolute,
          min: requestConstraints.pickupWindow.min,
          desired: requestConstraints.pickupWindow.desired,
          max: requestConstraints.pickupWindow.max,
        },
        dropoffConstraint: {
          type: TimeConstraintType.Absolute,
          min: requestConstraints.dropoffWindow.min,
          desired: requestConstraints.dropoffWindow.desired,
          max: requestConstraints.dropoffWindow.max,
        },
      }
    }

    throw new UnexpectedError('Invalid request constraints: at least one window must be defined')
  }
}
